import {IExtendedFilterValue} from "@app/interfaces/dtos/IExtendedFilterValue";
import {RouteLocation} from "vue-router";
import { getSearchRouteParams } from "@utils/route-helpers";
import {FilterType} from "@studyportals/omnisearch-interfaces";
import {IExtendedWithPortalTypeFilterValue} from "@app/routing/mappers/IExtendedWithPortalTypeFilterValue";
import {PortalType} from "@studyportals/domain-client";
import { SearchType } from "@domain/enums/SearchType";
import IExtendedSearchQueryFilter from "@app/interfaces/dtos/IExtendedSearchQueryFilter";


export abstract class FilterMapper {
	public abstract filterType: FilterType;
	public abstract readonly isMultiSelect: boolean;

	// Caps unbounded growth of dynamically fetched values (e.g. cities/organisations) on long-lived server singletons.
	private static readonly MAX_CACHED_VALUES = 1_000;

	protected abstract values: IExtendedWithPortalTypeFilterValue[];


	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public isMandatory(portalType: string): boolean {
		return false;
	}
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public showFilterForPortalType(portalType?: PortalType): boolean {
		return true;
	}

	public allowedInSearchType(searchType?: SearchType): boolean {
		if (!searchType || this.values.length === 0) return true;

		return this.values.some((value) => !value.excludedForSearchTypes?.includes(searchType));
	}

	public getAllExtendedFilterValues(portalType?: PortalType): IExtendedFilterValue[] {
		if (!portalType) return this.values;
		return this.values.filter(v => !v.portalTypes || v.portalTypes.includes(portalType));
	}

	public getImplicitFilterValues(portalType: PortalType): IExtendedWithPortalTypeFilterValue[] {
		return this.values.filter(v => v.implicitForPortalTypes?.includes(portalType));
	}

	public isImplicitForPortalType(valueId: string, portalType: PortalType): boolean {
		const value = this.values.find(v => v.id === valueId);
		return value?.implicitForPortalTypes?.includes(portalType) ?? false;
	}

	public isIndexable(value: string): boolean {
		const  currentFilterIndexability = this.values.find(v => v.id === value);

		return !currentFilterIndexability?.isNotIndexable;
	}

	public getExtendedFilterValueById(id: string): IExtendedFilterValue | undefined {
		return this.values.find((v) => v.id === id);
	}

	public getExtendedFilterValueByVirtualPath(virtualPath: string): IExtendedFilterValue | undefined {
		return this.values.find((v) => v.virtualPath === virtualPath);
	}

	public getAllVirtualPaths(includeSynonyms: boolean = false): string[] {
		const paths = [];

		for (const value of this.values) {
			if (value.virtualPath) paths.push(value.virtualPath);
			if (includeSynonyms && value.synonyms) paths.push(...value.synonyms);
		}

		return paths;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public getExtendedFilterValuesFromRoute(route: RouteLocation, processedFilters?: IExtendedSearchQueryFilter[]): IExtendedFilterValue[] | Promise<IExtendedFilterValue[]> {
		let valueSet = this.getFilterValuesFromRouteParams(new Set<IExtendedWithPortalTypeFilterValue>(), route)
		valueSet = this.getFilterValuesFromRouteQuery(valueSet, route);

		return Array.from(valueSet);
	}

	protected getFilterValuesFromRouteParams(valueSet: Set<IExtendedFilterValue>, route: RouteLocation): Set<IExtendedWithPortalTypeFilterValue> {
		const paramValue = getSearchRouteParams(route)[this.filterType];

		if (typeof paramValue === 'string') {
			let value = this.values.find((v) => v.virtualPath === paramValue);
			if (!value) value = this.values.find(v => v.synonyms?.includes(paramValue));

			if (value) valueSet.add(value);
		}

		return valueSet;
	}

	protected getFilterValuesFromRouteQuery(valueSet: Set<IExtendedFilterValue>, route: RouteLocation): Set<IExtendedWithPortalTypeFilterValue> {
		const queryValue = route.query?.[this.filterType];
		if (typeof queryValue === 'string') {
			const ids = queryValue.split(',');

			for (const id of ids) {
				const value = this.values.find((v) => v.id === id);
				if (value) valueSet.add(value);
			}
		}

		return valueSet
	}

	protected addToCacheWithCap(newValues: IExtendedWithPortalTypeFilterValue[]): void {
		if (newValues.length === 0) return;

		this.values.push(...newValues);

		const overflow = this.values.length - FilterMapper.MAX_CACHED_VALUES;
		if (overflow > 0) {
			this.values.splice(0, overflow);
		}
	}

}
